import { Group, Vector2, Vector2Tuple, Vector3 } from 'three';
import { Line2 } from 'three/addons/lines/Line2.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import { LineSegments2 } from 'three/addons/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/addons/lines/LineSegmentsGeometry.js';

import { SurfacePathTravel } from '../../math/index.js';
import {
  computeDirectedLines,
  computeLinesSegments,
  isVector2Tuple,
  toPositions,
} from '../../utils/index.js';

const autoClose = (points: Vector3[]) => {
  if (points.length === 0) {
    return [];
  }

  if (points[0].equals(points[points.length - 1])) {
    return points;
  }

  return [...points, points[0]];
};

type Resolution = Vector2 | Vector2Tuple | number[];
export const defaults = {
  contourColor: '#9a928a',
  pathColor: '#6f6f6f',
  innerPathColor: '#ff6000',
  outerPathColor: '#008cff',
  normalsColor: '#49ef40',
  tangentsColor: '#4c31d5',
  showTangents: true,
  showNormals: true,
  showInnerPath: true,
  showOuterPath: true,
  showPath: true,
  showContour: true,
} as const;

/**
 * Helper to visualize the path of a SurfacePathTravel
 *
 * @example
 * ```typescript
 * const path = new SurfacePathTravel();
 * const resolution = new Vector2(800, 600);
 * const helper = new SurfacePathTravelHelper(path, resolution);
 * scene.add(helper);
 * ```
 */
export class SurfacePathTravelHelper extends Group {
  lines: {
    tangents: LineSegments2[];
    normals: LineSegments2[];
    path: Line2[];
    innerPath: Line2[];
    outerPath: Line2[];
    contours: Line2[];
  } = {
    tangents: [],
    normals: [],
    contours: [],
    innerPath: [],
    outerPath: [],
    path: [],
  };

  set showTangents(visible: boolean) {
    this.lines.tangents.forEach((line) => {
      line.visible = visible;
    });
  }
  set showNormals(visible: boolean) {
    this.lines.normals.forEach((line) => {
      line.visible = visible;
    });
  }
  set showInnerPath(visible: boolean) {
    this.lines.innerPath.forEach((line) => {
      line.visible = visible;
    });
  }
  set showOuterPath(visible: boolean) {
    this.lines.outerPath.forEach((line) => {
      line.visible = visible;
    });
  }
  set showPath(visible: boolean) {
    this.lines.path.forEach((line) => {
      line.visible = visible;
    });
  }
  set showContour(visible: boolean) {
    this.lines.contours.forEach((line) => {
      line.visible = visible;
    });
  }

  set contourColor(value: string) {
    this.lines.contours.forEach((line) => {
      line.material.color.set(value);
    });
  }

  set pathColor(value: string) {
    this.lines.path.forEach((line) => {
      line.material.color.set(value);
    });
  }

  set innerPathColor(value: string) {
    this.lines.innerPath.forEach((line) => {
      line.material.color.set(value);
    });
  }

  set outerPathColor(value: string) {
    this.lines.outerPath.forEach((line) => {
      line.material.color.set(value);
    });
  }

  set normalsColor(value: string) {
    this.lines.normals.forEach((line) => {
      line.material.color.set(value);
    });
  }

  set tangentsColor(value: string) {
    this.lines.tangents.forEach((line) => {
      line.material.color.set(value);
    });
  }

  set resolution(value: Resolution) {
    this.setResolution(value);
  }

  constructor(path: SurfacePathTravel, resolution: Resolution, public divisions: number = 100) {
    super();

    this.setPath(path);
    this.setResolution(resolution);
  }

  setResolution(value: Resolution) {
    Object.values(this.lines)
      .flat()
      .forEach((line) => {
        line.material.resolution = isVector2Tuple(value)
          ? new Vector2().fromArray(value)
          : (value as Vector2);
      });
  }

  setPath(path: SurfacePathTravel) {
    this.clear();

    const curves = path.getCurves();
    const resolution = new Vector2(1, 1);
    const linewidth = 4;

    this.lines.contours = path.contours.map((contour) => {
      const contourLine = new Line2(
        new LineGeometry().setPositions(toPositions(autoClose(contour))),
        new LineMaterial({
          color: defaults.contourColor,
          linewidth: linewidth,
          vertexColors: false,
          resolution,
        })
      );

      return contourLine;
    });

    curves.forEach((curve) => {
      const { points, normals, inners, outers, tangents } = curve.getPointsArray(this.divisions);

      const innerLine = new Line2(
        new LineGeometry().setPositions(toPositions(inners)),
        new LineMaterial({
          color: defaults.innerPathColor,
          linewidth: linewidth,
          vertexColors: false,
          resolution,
        })
      );

      this.lines.innerPath.push(innerLine);

      const outerLine = new Line2(
        new LineGeometry().setPositions(toPositions(outers)),
        new LineMaterial({
          color: defaults.outerPathColor,
          linewidth: linewidth,
          vertexColors: false,
          resolution,
        })
      );

      this.lines.outerPath.push(outerLine);

      const pathLine = new Line2(
        new LineGeometry().setPositions(toPositions(points)),
        new LineMaterial({
          color: defaults.pathColor,
          linewidth: linewidth * 0.5,
          vertexColors: false,
          resolution,
        })
      );

      this.lines.path.push(pathLine);

      const normalsLine = new LineSegments2(
        new LineSegmentsGeometry().setPositions(
          toPositions(computeLinesSegments(computeDirectedLines(points, normals)))
        ),
        new LineMaterial({
          color: defaults.normalsColor,
          linewidth: linewidth * 0.25,
          vertexColors: false,
          resolution,
        })
      );

      this.lines.normals.push(normalsLine);

      const tangentsLine = new LineSegments2(
        new LineSegmentsGeometry().setPositions(
          toPositions(computeLinesSegments(computeDirectedLines(points, tangents)))
        ),
        new LineMaterial({
          color: defaults.tangentsColor,
          linewidth: linewidth * 0.25,
          vertexColors: false,
          resolution,
        })
      );

      this.lines.tangents.push(tangentsLine);
    });

    Object.values(this.lines)
      .flat()
      .forEach((line) => {
        line.computeLineDistances();
        line.scale.set(1, 1, 1);
        this.add(line);
      });
  }
}
